const API_URL = process.env.REACT_APP_API_URL;
const authenticationHeaders = token =>
  typeof token === 'string' ? {Authorization: `Bearer ${token}`} : {'X-WP-Nonce': token.nonce};

const endpoint = path =>
  `${String(API_URL || '').replace(/\/$/, '')}/mco-leclub-api/v1/assistant${path}`;

export class AssistantError extends Error {
  constructor(message, {status = 0, code = 'network_error', resetRequired = false} = {}) {
    super(message);
    this.name = 'AssistantError';
    this.status = status;
    this.code = code;
    this.resetRequired = resetRequired;
  }
}

const request = async (path, token, {method = 'GET', body, signal, keepalive = false} = {}) => {
  let response;
  try {
    response = await fetch(endpoint(path), {
      method,
      body: body === undefined ? undefined : JSON.stringify(body),
      headers: {
        Accept: 'application/json',
        ...authenticationHeaders(token),
        ...(body === undefined ? {} : {'Content-Type': 'application/json'}),
      },
      credentials: 'same-origin',
      cache: 'no-store',
      redirect: 'error',
      referrerPolicy: 'same-origin',
      keepalive,
      signal,
    });
  } catch (error) {
    if (error.name === 'AbortError') throw error;
    throw new AssistantError(
      navigator.onLine
        ? 'Le conseiller est momentanément indisponible. Vous pourrez réessayer.'
        : 'Vous devez être en ligne pour utiliser le conseiller.',
    );
  }

  const payload = response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) {
    throw new AssistantError(payload?.message || 'Le conseiller est momentanément indisponible.', {
      status: response.status,
      code: payload?.code,
      resetRequired: Boolean(payload?.reset_required),
    });
  }
  return payload;
};

export const getAssistantConfig = (token, signal) => request('/config', token, {signal});

export const sendAssistantMessage = (token, body, signal) =>
  request('/messages', token, {method: 'POST', body, signal});

export const getAssistantImage = async (token, imageId, signal) => {
  let response;
  try {
    response = await fetch(endpoint(`/images/${encodeURIComponent(imageId)}`), {
      headers: {Accept: 'image/png,image/jpeg,image/webp', ...authenticationHeaders(token)},
      credentials: 'same-origin',
      cache: 'no-store',
      redirect: 'error',
      referrerPolicy: 'same-origin',
      signal,
    });
  } catch (error) {
    if (error.name === 'AbortError') throw error;
    throw new AssistantError('Le visuel généré ne peut pas être chargé.');
  }
  const contentType = String(response.headers.get('content-type') || '').split(';')[0];
  const declaredSize = Number(response.headers.get('content-length') || 0);
  if (
    !response.ok ||
    !['image/png', 'image/jpeg', 'image/webp'].includes(contentType) ||
    (declaredSize && declaredSize > 8 * 1024 * 1024)
  ) {
    throw new AssistantError('Le visuel généré a expiré ou ne peut pas être chargé.', {
      status: response.status,
    });
  }
  const blob = await response.blob();
  if (blob.size === 0 || blob.size > 8 * 1024 * 1024 || blob.type !== contentType) {
    throw new AssistantError('Le visuel généré est invalide.');
  }
  return blob;
};

export const resetAssistantConversation = (token, sessionId, {keepalive = false} = {}) =>
  request('/conversations', token, {
    method: 'POST',
    body: {session_id: sessionId},
    keepalive,
  });
